import { motion } from 'framer-motion'
import { Newspaper, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { NewsCard } from './NewsCard'
import { MoreNewsButton } from './MoreNewsButton'
import { API_BASE_URL } from '../lib/api'
import { useBackendHealth } from '../lib/backendHealth'

interface NewsArticle {
  id: string // Changed from number to string (UUID)
  title: string
  summary: string
  imageUrl: string
  source: string
  url: string
  publishedAt: string
  hasVideo?: boolean
  videoUrl?: string
}

export function NewsSection() {
  const navigate = useNavigate()
  const [articles, setArticles] = useState<NewsArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check backend availability
  const { isAvailable: isBackendAvailable, isChecking: isCheckingBackend } = useBackendHealth(
    API_BASE_URL,
    {
      enabled: true, // Always enable health checks
      checkInterval: 0 // No periodic checks (check once on mount)
    }
  )

  const handleMoreNewsClick = () => {
    // Track event with Plausible analytics (if configured)
    if (window.plausible) {
      try {
        window.plausible('more_news_click', {
          props: {
            location: 'homepage_news_section'
          }
        })
      } catch (error) {
        // Silently fail - analytics failures shouldn't break functionality
        console.warn('Failed to track analytics event:', error)
      }
    }

    navigate('/news')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    // Only fetch news if backend is available
    if (isBackendAvailable === false) {
      console.log('[NewsSection] Backend unavailable, skipping news fetch')
      setLoading(false)
      setError(null) // Clear any errors
      return
    }

    // Still checking backend availability, wait
    if (isCheckingBackend) {
      console.log('[NewsSection] Checking backend availability...')
      return
    }

    const fetchNews = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetch(`${API_BASE_URL}/api/news?limit=6`)

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`)
        }

        const result = await response.json()

        if (!result.success) {
          throw new Error(result.error || 'Failed to fetch articles')
        }

        setArticles(result.data || [])
        setLoading(false)
      } catch (err) {
        console.error('[NewsSection] Failed to fetch articles:', err)
        setError('Failed to load news articles. Please try again later.')
        setLoading(false)
      }
    }

    fetchNews()
  }, [isBackendAvailable, isCheckingBackend])

  // While checking backend availability, show nothing (section will be hidden)
  if (isCheckingBackend) {
    console.log('[NewsSection] Backend health check in progress, not rendering section')
    return null
  }

  // If backend is unavailable, hide the entire section
  if (isBackendAvailable === false) {
    console.log('[NewsSection] Backend unavailable, hiding AI News section')
    return null
  }

  return (
    <section id="news" className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Newspaper className="w-10 h-10 text-blue-600 dark:text-orange-400" />
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
              AI News & Insights
            </h2>
          </div>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Stay updated with the latest developments in AI technology and industry trends.
            Curated news from leading sources worldwide.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading latest news...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* News Grid */}
        {!loading && !error && articles.length > 0 && (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {articles.map((article, index) => (
                <NewsCard key={article.id} article={article} index={index} />
              ))}
            </div>

            {/* More News Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex justify-center mt-8 mb-8"
            >
              <MoreNewsButton onClick={handleMoreNewsClick} />
            </motion.div>
          </>
        )}

      </div>
    </section>
  )
}
